import { useEffect, useRef, useState } from "react";
import type { PictureVideoStatus, WorkStep } from "@studio/shared";
import { InlineSpinner } from "../components/InlineSpinner";
import { PictureVideoPanel, usePictureVideo } from "../components/PictureVideoPanel";
import { formatDuration } from "../components/TrackCard";
import { ErrorPage, InlineError, LoadingPage, StudioShell } from "../components/StudioShell";
import { ApiError, apiRequest, useWork } from "../lib/api";
import { navigate } from "../lib/navigation";
import { pictureSummary } from "../lib/picture";
import { loadSession, pathForStep } from "../lib/session";

/**
 * 그림 수업 화면 5 · 작품 카드(D2) — 그림 + (선택) 노래.
 *
 * 🚨 재생처럼 생긴 것은 **그림 위 ▶ 하나뿐**이고, 그것이 진짜로 재생한다
 *    (`no-fake-play-affordance.test.ts` 의 약속). 그래서 `<audio>` 는 보이지 않게 두고
 *    컨트롤을 겹쳐 그리지 않는다 — ▶ 와 플레이어가 둘 다 있으면 학생은 둘 중 하나를 못 믿는다.
 * 🔑 영상(mp4)으로 합치지 않는다 — 서버 합치기(compose)가 없어 바로 뜬다(starter OOM 회피).
 */
export function PictureDonePage({ workId }: { workId: string }) {
  const query = useWork(workId);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [playing, setPlaying] = useState(false);
  const [busy, setBusy] = useState<"download" | "video" | "back" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const audio = useRef<HTMLAudioElement | null>(null);
  const name = loadSession()?.name ?? "나";

  const imageAssetId = query.data?.cuts[0]?.image_asset_id ?? null;
  const musicAssetId = query.data?.work.music_source?.asset_id ?? null;
  const pictureVideo = usePictureVideo(workId, imageAssetId);
  // 🔑 jery 결정 2: 영상이 있으면 그림 대신 영상을 보여 준다(소리는 끄고 되풀이 — 노래는 ▶ 가 튼다).
  const clipUrl = pictureVideo.status?.clip_url ?? null;

  useEffect(() => {
    if (!imageAssetId) return undefined;
    let cancelled = false;
    void apiRequest<{ url: string }>(`/api/assets/${encodeURIComponent(imageAssetId)}/url`)
      .then((result) => { if (!cancelled) setImageUrl(result.url); })
      .catch(() => { if (!cancelled) setError("그림을 불러오지 못했어요. 새로고침해 주세요."); });
    return () => { cancelled = true; };
  }, [imageAssetId]);

  useEffect(() => {
    setAudioUrl(null);
    setPlaying(false);
    if (!musicAssetId) return undefined;
    let cancelled = false;
    void apiRequest<{ url: string }>(`/api/assets/${encodeURIComponent(musicAssetId)}/url`)
      .then((result) => { if (!cancelled) setAudioUrl(result.url); })
      .catch(() => { if (!cancelled) setError("노래를 불러오지 못했어요. 새로고침해 주세요."); });
    return () => { cancelled = true; };
  }, [musicAssetId]);

  const showToast = (message: string): void => {
    setToast(message);
    window.setTimeout(() => setToast(null), 2_500);
  };

  if (query.loading) return <LoadingPage label="작품 카드를 불러오는 중..." />;
  if (!query.data || query.error) {
    return <ErrorPage message={query.error?.message ?? "작품 카드를 불러오지 못했습니다."} />;
  }
  const data = query.data;
  const source = data.work.music_source;
  const choices = data.work.picture;
  const alt = `${name}의 그림${choices ? `: ${pictureSummary(choices)}` : ""}`;

  const togglePlay = async (): Promise<void> => {
    const player = audio.current;
    if (!player) return;
    if (player.paused) {
      try {
        await player.play();
      } catch {
        setError("노래를 틀지 못했어요. 다시 눌러 주세요.");
      }
    } else {
      player.pause();
    }
  };

  const download = async (): Promise<void> => {
    setBusy("download");
    setError(null);
    try {
      // 🔑 R2 에 CORS 가 없어 fetch 로 받지 않는다 — 서명 주소(첨부 파일명 포함)로 최상위 이동.
      const result = await apiRequest<{ url: string }>(`/api/works/${encodeURIComponent(workId)}/picture/download`);
      const anchor = document.createElement("a");
      anchor.href = result.url;
      anchor.rel = "noopener";
      anchor.click();
      showToast("저장을 시작했어요");
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "그림을 저장하지 못했어요.");
    } finally {
      setBusy(null);
    }
  };

  /** 🔑 누를 때 새 서명 주소를 받는다 — 화면을 10분 넘게 열어 두면 처음 받은 주소가 만료된다(Codex 검수). */
  const downloadVideo = async (): Promise<void> => {
    setBusy("video");
    setError(null);
    try {
      const fresh = await apiRequest<PictureVideoStatus>(`/api/works/${encodeURIComponent(workId)}/picture/video`);
      if (!fresh.clip_download_url) throw new Error("no clip");
      const anchor = document.createElement("a");
      anchor.href = fresh.clip_download_url;
      anchor.rel = "noopener";
      anchor.click();
      showToast("저장을 시작했어요");
    } catch {
      setError("영상을 저장하지 못했어요. 다시 눌러 주세요.");
    } finally {
      setBusy(null);
    }
  };

  const goBack = async (step: Extract<WorkStep, "pic_image" | "pic_song">): Promise<void> => {
    setBusy("back");
    setError(null);
    try {
      audio.current?.pause();
      await apiRequest(`/api/works/${encodeURIComponent(workId)}/autosave`, {
        method: "POST",
        body: JSON.stringify({ step, gifts_v: 2 }),
      });
      navigate(pathForStep(workId, step));
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "돌아가지 못했어요.");
      setBusy(null);
    }
  };

  return (
    <StudioShell currentStep={4} imageRegenRemaining={data.image_regen_remaining}>
      <div className="done-page-content">
        <div className="success-icon" aria-hidden="true">✓</div>
        <h1 tabIndex={-1}>{source ? "나만의 작품을 완성했어요" : "나만의 그림을 완성했어요"}</h1>
        <p className="done-song-meta">{name} · {source ? "그림 + 노래" : "그림"}</p>
        <article className="pic-artwork">
          {source ? (
            <button
              type="button"
              className="pic-art-play"
              data-playing={playing}
              aria-label={playing ? "노래 멈추기" : "노래 듣기"}
              aria-pressed={playing}
              disabled={!audioUrl}
              onClick={() => void togglePlay()}
            >
              {clipUrl
                ? <video src={clipUrl} poster={imageUrl ?? undefined} aria-label={alt} muted loop autoPlay playsInline />
                : imageUrl ? <img src={imageUrl} alt={alt} /> : <span className="pic-art-empty" aria-hidden="true" />}
              <span className="pic-play-badge" aria-hidden="true">{audioUrl ? (playing ? "❚❚" : "▶") : "…"}</span>
            </button>
          ) : (
            <div className="pic-art-still">
              {clipUrl
                ? <video src={clipUrl} poster={imageUrl ?? undefined} aria-label={alt} muted loop autoPlay playsInline />
                : imageUrl ? <img src={imageUrl} alt={alt} /> : <span className="pic-art-empty" aria-hidden="true" />}
            </div>
          )}
          {source ? (
            <div className="track-card-heading pic-art-meta">
              <div>
                <h3>{source.title}</h3>
                <p>{name} · {formatDuration(Math.round(source.duration_seconds))} · 그림을 누르면 노래가 나와요</p>
              </div>
            </div>
          ) : null}
          {audioUrl ? (
            <audio
              ref={audio}
              src={audioUrl}
              preload="none"
              onPlay={() => setPlaying(true)}
              onPause={() => setPlaying(false)}
              onEnded={() => setPlaying(false)}
            />
          ) : null}
        </article>
        <PictureVideoPanel workId={workId} video={pictureVideo} disabled={busy !== null} />
        <div className="done-actions">
          <button className="button-primary" type="button" disabled={busy !== null || !imageUrl} onClick={() => void download()}>
            {busy === "download" ? <InlineSpinner label="저장 준비 중..." /> : "그림 저장"}
          </button>
          {clipUrl ? (
            <button className="button-secondary" type="button" disabled={busy !== null} onClick={() => void downloadVideo()}>
              {busy === "video" ? <InlineSpinner label="저장 준비 중..." /> : "영상 저장"}
            </button>
          ) : null}
          {source ? (
            <button className="button-secondary" type="button" disabled={busy !== null} onClick={() => void goBack("pic_image")}>
              그림 다시 만들기
            </button>
          ) : (
            <button className="button-secondary" type="button" disabled={busy !== null} onClick={() => void goBack("pic_song")}>
              노래 붙이기
            </button>
          )}
        </div>
        {source ? (
          <p className="pic-center">
            <button className="button-ghost" type="button" disabled={busy !== null} onClick={() => void goBack("pic_song")}>
              노래 바꾸기
            </button>
          </p>
        ) : null}
        {error ? <InlineError>{error}</InlineError> : null}
        {toast ? <div className="toast" role="status">{toast} ✓</div> : null}
      </div>
    </StudioShell>
  );
}
